import { TextField } from '@material-ui/core';
import moment, { Moment } from 'moment';
import React from 'react';
import { MuiPickersUtilsProvider, DatePicker as MuiDatePicker, DateTimePicker as MuiDateTimePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

import { isDesktop, isIOS, isMobileDevice } from '../../utilities/is_mobile';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { getBrowserName } from '../../utilities/browser';
const isSafari = isDesktop() && (getBrowserName() === 'Safari');


interface Props {
	label: string;
	fullWidth?: boolean;
	disabled?: boolean;
	startOfDay?: boolean;
	endOfDay?: boolean;
	minDate?: Date;
	maxDate?: Date;
	useUtc?: boolean;
	meta: {
		dirty?: boolean;
		error?: string;
		invalid?: boolean;
		pristine?: boolean;
		touched?: boolean;
		valid?: boolean;
		visited?: boolean;
		warning?: string;
	};
	input: {
		value: Date | moment.Moment | undefined | string;
		onChange: (value: Date | moment.Moment | string) => void;
		onBlur?: (event: any) => void;
	};
	style?: React.CSSProperties;
	useMoment?: boolean;
	dateAndTime?: boolean;
}

interface State {
	internalValue: string;
}

const labelConfig = { shrink: true };

class DatePicker extends React.PureComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			internalValue: this.formatDateValue(props.input.value, props.useUtc, props.dateAndTime),
		};
	}

	componentDidUpdate(prevProps: Readonly<Props>): void {
		// Sync from props when external value changes
		if (prevProps.input.value !== this.props.input.value) {
			this.setState({
				internalValue: this.formatDateValue(this.props.input.value, this.props.useUtc, this.props.dateAndTime),
			});
		}
	}

	getDateFormat = (dateAndTime?: boolean): string => {
		return isIOS && dateAndTime
			? 'MM/DD/YYYY hh:mm A'
			: isSafari 
				? 'MM/DD/YYYY' 
				: 'YYYY-MM-DD';
	};

	formatDateValue = (value: Date | moment.Moment | undefined | string, useUtc?: boolean, dateAndTime?: boolean): string => {
		if (!value) return '';
		
		const dateFormat = this.getDateFormat(dateAndTime);
		
		if (useUtc) {
			return moment.utc(value).format(dateFormat);
		}
		return moment(value).format(dateFormat);
	};

	checkValue = (val: Moment | null) => {
		if (val?.isValid()) {
			if (this.props.startOfDay) {
				val = val.startOf('day');
			} else if (this.props.endOfDay) {
				val = val.endOf('day');
			}
			const dateVal = this.props.useMoment ? val : val.toDate();
			this.props.input.onChange(dateVal);
		}
	};

	parseDateString = (dateString: string): Moment => {
		const dateFormat = this.getDateFormat(this.props.dateAndTime);
		return this.props.useUtc 
			? moment.utc(dateString, dateFormat)
			: moment(dateString, dateFormat);
	};

	handleDateChange = (dateString: string) => {
		const val = this.parseDateString(dateString);
		this.checkValue(val);
	};

	onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const dateString = event.target.value;
		this.setState({ internalValue: dateString });
	};

	onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		this.handleDateChange(event.target.value);
		// Call redux-form's onBlur to mark field as touched
		if (this.props.input.onBlur) {
			this.props.input.onBlur(event);
		}
	};

	muiDateChange = (date: MaterialUiPickersDate) => {
		this.checkValue(date);
	};

	render() {
		const {
			meta,
			input,
			minDate,
			maxDate,
			startOfDay,
			endOfDay,
			useUtc,
			useMoment,
			dateAndTime,
			...config
		} = this.props;
		const { error, warning, touched, invalid } = meta;
		const errorText = touched && invalid && (error || warning);
		const errorCheck = !!errorText;
		const dateFormat = this.getDateFormat(dateAndTime);
		const inputProps: any = {};
		if (minDate) inputProps.min = moment(minDate).format(dateFormat);
		if (maxDate) inputProps.max = moment(maxDate).format(dateFormat);
		const trueValue = this.formatDateValue(input.value, useUtc, dateAndTime);

		const type = isIOS && dateAndTime
			? 'datetime-local'
			: 'date';

		return isMobileDevice
			? (
				<MuiPickersUtilsProvider utils={MomentUtils}>
					{
						dateAndTime
							? <MuiDateTimePicker
								{...config}
								error={errorCheck}
								helperText={errorText}
								value={trueValue}
								onChange={this.muiDateChange}
								inputProps={inputProps}
								format={dateFormat}
							/>
							: <MuiDatePicker
								{...config}
								error={errorCheck}
								helperText={errorText}
								value={trueValue}
								onChange={this.muiDateChange}
								inputProps={inputProps}
								format={dateFormat}
							/>
					}
				</MuiPickersUtilsProvider>
			)
			: (
				<TextField
					{...config}
					error={errorCheck}
					helperText={errorText}
					value={this.state.internalValue}
					onChange={this.onChange}
					onBlur={this.onBlur}
					inputProps={inputProps}
					InputLabelProps={labelConfig}
					type={type}
				/>
			);
	}
}

export default DatePicker;
